import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Avviso, Pie } from '../componenti/Pezzi.tsx'
import { Bottone, Campo } from '../componenti/base.tsx'
import { CampoNomeUtente, indirizzoCompleto, useDominio } from '../componenti/CampoNomeUtente.tsx'
import { EntraConComputer } from '../componenti/EntraConComputer.tsx'

type Modo = 'password' | 'collegamento' | 'codice'

/**
 * Porta il foco sul campo al momento dell'inserimento nel documento.
 * La funzione e' dichiarata fuori dal componente: l'identita' resta stabile fra un disegno
 * e l'altro e il riferimento non viene riagganciato a ogni battuta.
 */
function focoIniziale(elemento: HTMLInputElement | null) {
  elemento?.focus()
}

/**
 * Si arriva dal messenger (479): GET /api/cas/login rimanda a /messenger/accesso chi non ha ancora
 * una sessione completa, e dopo l'accesso main.tsx torna al messenger da solo. Lo si dice prima.
 */
function AvvisoMessenger() {
  if (window.location.pathname !== '/messenger/accesso') return null
  return <Avviso>Entra con le credenziali del gestionale: dopo l’accesso torni al messenger.</Avviso>
}

/**
 * Accesso all'applicazione. Tre metodi previsti dal contratto: password, collegamento
 * inviato per posta elettronica e codice temporaneo. Il secondo fattore, quando e' attivo,
 * viene verificato dopo il primo metodo: il codice dell'app oppure, dal 31/10/2026 (493), un
 * codice di recupero, che vale una volta sola.
 *
 * Dall'1/11/2026 (520) il campo dice «Nome utente o email», con il dominio di CIDIS accanto
 * (componenti/CampoNomeUtente.tsx), e «Ho dimenticato la password» apre una richiesta che decide chi
 * gestisce gli utenti: il collegamento arriva poi all'email personale di recupero.
 *
 * Sul computer ricordato (531) in cima c'e' «Entra con Windows Hello» o «con Touch ID»
 * (componenti/EntraConComputer.tsx); le linguette restano sotto, per chi entra con la password.
 */
export function Accesso({ secondoFattore = false }: { secondoFattore?: boolean } = {}) {
  const { aggiorna } = useSessione()
  const vai = useNavigate()
  const [modo, setModo] = useState<Modo>('password')
  const [email, setEmail] = useState('')
  const { domini, dominio, scegli } = useDominio()
  /** Quello che si manda all'API: il nome utente con il dominio scelto, o l'indirizzo scritto per intero. */
  const indirizzo = indirizzoCompleto(email, dominio)
  /** «Ho dimenticato la password»: la pagina della richiesta, e la risposta, che e' uguale per tutti. */
  const [dimenticata, setDimenticata] = useState(false)
  const [richiestaFatta, setRichiestaFatta] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [codice, setCodice] = useState('')
  const [attesaCodice, setAttesaCodice] = useState(false)
  const [mfa, setMfa] = useState(secondoFattore)
  const [conRecupero, setConRecupero] = useState(false)
  const [recupero, setRecupero] = useState('')
  /** Entrato con un codice di recupero: quanti ne restano, da dire prima di aprire il gestionale. */
  const [rimasti, setRimasti] = useState<number | null>(null)
  const [messaggio, setMessaggio] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function esegui(azione: () => Promise<void>) {
    setInCorso(true); setErrore(null)
    try { await azione() }
    catch (e) { setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.') }
    finally { setInCorso(false) }
  }

  const entraConPassword = () => esegui(async () => {
    const esito = await api.post<{ mfaRichiesta: boolean }>('/auth/accesso', { email: indirizzo, password })
    if (esito.mfaRichiesta) setMfa(true); else await aggiorna()
  })

  const chiediCollegamento = () => esegui(async () => {
    await api.post('/auth/magic-link', { email: indirizzo })
    setMessaggio('Se l’indirizzo è registrato, viene inviato un messaggio con il collegamento. Il collegamento vale quindici minuti.')
  })

  const chiediCodice = () => esegui(async () => {
    await api.post('/auth/otp/richiedi', { email: indirizzo })
    setAttesaCodice(true)
    setMessaggio('Se l’indirizzo è registrato, viene inviato un codice di sei cifre. Il codice vale dieci minuti.')
  })

  const verificaCodice = () => esegui(async () => {
    const esito = await api.post<{ mfaRichiesta: boolean }>('/auth/otp/verifica', { email: indirizzo, codice })
    if (esito.mfaRichiesta) setMfa(true); else await aggiorna()
  })

  const verificaMfa = () => esegui(async () => {
    await api.post('/auth/mfa/verifica', { codice })
    await aggiorna()
  })

  const usaRecupero = () => esegui(async () => {
    const esito = await api.post<{ rimasti: number }>('/auth/mfa/recupero', { codice: recupero })
    setRimasti(esito.rimasti)
  })

  const chiediNuovaPassword = () => esegui(async () => {
    const esito = await api.post<{ esito: string }>('/auth/password/richiedi', { email: indirizzo })
    setRichiestaFatta(esito.esito)
  })

  if (dimenticata) {
    // «Ho dimenticato la password» (520): niente collegamento immediato. La richiesta va a chi gestisce
    // gli utenti, e la risposta e' la stessa che l'utenza esista o no.
    return (
      <div className="accesso">
        <div className="riquadro">
          <div className="scheda">
            <div className="stemma">
              <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
            </div>
            <h1>Password dimenticata</h1>
            <p className="occhiello">La reimpostazione la approva chi gestisce gli utenti</p>
            {errore && <Avviso tipo="errore">{errore}</Avviso>}
            {richiestaFatta
              ? <Avviso tipo="informazione">{richiestaFatta}</Avviso>
              : (
                <form onSubmit={(e) => { e.preventDefault(); void chiediNuovaPassword() }}>
                  <CampoNomeUtente valore={email} cambia={setEmail} domini={domini} dominio={dominio}
                                   scegliDominio={scegli} riferimento={focoIniziale} />
                  <p className="accesso-nota">
                    La richiesta arriva a chi gestisce gli utenti. Quando la approva, il collegamento per scegliere
                    la password nuova arriva alla tua email personale di recupero. Ti avvisiamo anche sulla casella
                    CIDIS.
                  </p>
                  <Bottone tipo="submit" variante="primario" larghezzaPiena inCorso={inCorso}
                           disabled={!email.trim()}>
                    Chiedi la reimpostazione
                  </Bottone>
                </form>
              )}
            <div className="accesso-uscita">
              <Bottone variante="discreto" larghezzaPiena
                       onClick={() => { setDimenticata(false); setRichiestaFatta(null); setErrore(null) }}>
                Torna all’accesso
              </Bottone>
            </div>
          </div>
          <Pie />
        </div>
      </div>
    )
  }

  if (mfa && rimasti !== null) {
    // Entrato con un codice di recupero: si dice quanti ne restano e dove sostituire l'app.
    return (
      <div className="accesso">
        <div className="riquadro">
          <div className="scheda">
            <div className="stemma">
              <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
            </div>
            <h1>Sei entrato</h1>
            <p className="occhiello">Con un codice di recupero</p>
            <Avviso tipo={rimasti <= 3 ? 'attenzione' : 'informazione'}>
              Il codice che hai usato non vale più. {rimasti === 0 ? 'Non ti restano codici di recupero.'
                : rimasti === 1 ? 'Ti resta un solo codice di recupero.' : `Ti restano ${rimasti} codici di recupero.`}
              {' '}Se hai perso il telefono, in «Secondo fattore» sostituisci l’app e genera codici nuovi.
            </Avviso>
            <Bottone variante="primario" larghezzaPiena
                     onClick={() => { vai('/secondo-fattore'); void aggiorna() }}>
              Vai a «Secondo fattore»
            </Bottone>
            <div className="accesso-uscita">
              <Bottone variante="discreto" larghezzaPiena onClick={() => { void aggiorna() }}>
                Continua
              </Bottone>
            </div>
          </div>
          <Pie />
        </div>
      </div>
    )
  }

  if (mfa) {
    return (
      <div className="accesso">
        <div className="riquadro">
          <div className="scheda">
            <div className="stemma">
              <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
            </div>
            <h1>Secondo fattore</h1>
            <p className="occhiello">
              {conRecupero ? 'Uno dei codici di recupero che hai conservato' : 'Codice di sei cifre dall’app di autenticazione'}
            </p>
            <AvvisoMessenger />
            {errore && <Avviso tipo="errore">{errore}</Avviso>}
            {conRecupero ? (
              <form onSubmit={(e) => { e.preventDefault(); usaRecupero() }}>
                <Campo
                  etichetta="Codice di recupero"
                  valore={recupero}
                  cambia={setRecupero}
                  autoComplete="off"
                  maxLength={20}
                  obbligatorio
                  riferimento={focoIniziale}
                  aiuto="Dieci lettere e cifre, per esempio k7m2p-xq9ra. Vale una volta sola."
                />
                <Bottone tipo="submit" variante="primario" larghezzaPiena
                         inCorso={inCorso} disabled={recupero.replace(/[^a-z0-9]/gi, '').length !== 10}>
                  Entra
                </Bottone>
              </form>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); verificaMfa() }}>
                <div className="campo-codice">
                  <Campo
                    etichetta="Codice"
                    valore={codice}
                    cambia={(v) => setCodice(v.replace(/\D/g, ''))}
                    autoComplete="one-time-code"
                    maxLength={6}
                    obbligatorio
                    riferimento={focoIniziale}
                  />
                </div>
                <Bottone tipo="submit" variante="primario" larghezzaPiena
                         inCorso={inCorso} disabled={codice.length !== 6}>
                  Entra
                </Bottone>
              </form>
            )}
            <div className="fattore-alternativa">
              <Bottone variante="discreto" dimensione="piccolo"
                       onClick={() => { setConRecupero((v) => !v); setErrore(null) }}>
                {conRecupero ? 'Usa il codice dell’app' : 'Usa un codice di recupero'}
              </Bottone>
            </div>
            <div className="accesso-uscita">
              <Bottone variante="discreto" larghezzaPiena
                       onClick={() => void api.post('/auth/esci').then(() => {
                         // Si torna alla prima pagina dell'accesso: senza, dopo «Esci» restava questa.
                         setMfa(false); setConRecupero(false); setCodice(''); setRecupero('')
                         return aggiorna()
                       })}>
                Esci
              </Bottone>
            </div>
          </div>
          <Pie />
        </div>
      </div>
    )
  }

  return (
    <div className="accesso">
      <div className="riquadro">
        <div className="scheda">
          <div className="stemma">
            <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
          </div>
          <h1>Gestionale C.I.D.I.S.</h1>
          <p className="occhiello">Impresa Sociale ETS</p>

          <EntraConComputer />

          <div className="linguette linguette-ripartite" role="tablist"
               aria-label="Modalità di accesso">
            {([['password', 'Password'], ['collegamento', 'Collegamento'], ['codice', 'Codice']] as const)
              .map(([valore, etichetta]) => (
                <button key={valore} type="button" role="tab" aria-selected={modo === valore}
                        onClick={() => { setModo(valore); setMessaggio(null); setErrore(null); setAttesaCodice(false) }}>
                  {etichetta}
                </button>
              ))}
          </div>

          <AvvisoMessenger />
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          {messaggio && <Avviso tipo="informazione">{messaggio}</Avviso>}

          <form onSubmit={(e) => {
            e.preventDefault()
            if (modo === 'password') entraConPassword()
            else if (modo === 'collegamento') chiediCollegamento()
            else if (attesaCodice) verificaCodice()
            else chiediCodice()
          }}>
            <CampoNomeUtente valore={email} cambia={setEmail} domini={domini} dominio={dominio}
                             scegliDominio={scegli} riferimento={focoIniziale} />

            {modo === 'password' && (
              <>
                <Campo
                  etichetta="Password"
                  tipo="password"
                  valore={password}
                  cambia={setPassword}
                  autoComplete="current-password"
                  obbligatorio
                />
                <div className="accesso-recupero">
                  <Bottone variante="discreto" dimensione="piccolo"
                           onClick={() => { setDimenticata(true); setRichiestaFatta(null); setErrore(null); setMessaggio(null) }}>
                    Ho dimenticato la password
                  </Bottone>
                </div>
              </>
            )}

            {modo === 'codice' && attesaCodice && (
              <div className="campo-codice">
                <Campo
                  etichetta="Codice ricevuto"
                  valore={codice}
                  cambia={(v) => setCodice(v.replace(/\D/g, ''))}
                  autoComplete="one-time-code"
                  maxLength={6}
                  obbligatorio
                />
              </div>
            )}

            <Bottone tipo="submit" variante="primario" larghezzaPiena inCorso={inCorso}>
              {modo === 'password' ? 'Entra'
                : modo === 'collegamento' ? 'Invia il collegamento'
                : attesaCodice ? 'Entra' : 'Invia il codice'}
            </Bottone>
          </form>

          <p className="accesso-nota">
            Gli accessi sono registrati. Un tentativo non riconosciuto va segnalato
            all’amministratore.
          </p>
        </div>
        <Pie />
      </div>
    </div>
  )
}
